import { createSlice } from "@reduxjs/toolkit";
import { SoilProjectResponse } from "../data/api/soil/types";
import { SRecoverable, SSa2Wrapper } from "../data/Recoverable";
import { VegProjectResponse } from "../data/api/veg/types";

export type ProjectAnnualEmissions = { year: number; annualEmissions: number };

export type ComponentAnnualResponse = {
  projectAnnualEmissions: Array<ProjectAnnualEmissions>;
  projectAverageEmissions: number;
  projectStdDevOrTolEmissions: number;
};

export type ComponentTotalResponse = { projectEmissions: number };

// Not every request takes an SA2. Those that do not are aggregated over the entire area, and succeed or fail as a single call.
export type Queries = {
  baselineLivestock?: SRecoverable<Array<SSa2Wrapper<ComponentAnnualResponse>>>;
  baselineFertiliser?: SRecoverable<Array<SSa2Wrapper<ComponentTotalResponse>>>;
  baselineLime?: SRecoverable<SSa2Wrapper<ComponentAnnualResponse>>;
  baselineResidues?: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
  baselineIrrigation?: SRecoverable<
    Array<SSa2Wrapper<ComponentAnnualResponse>>
  >;
  projectLivestock?: SRecoverable<Array<SSa2Wrapper<ComponentAnnualResponse>>>;
  projectFertiliser?: SRecoverable<Array<SSa2Wrapper<ComponentTotalResponse>>>;
  projectLime?: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
  projectResidues?: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
  projectIrrigation?: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
  project?: SRecoverable<SoilProjectResponse>;
  vegProject?: SRecoverable<VegProjectResponse>;
};

const initialState: Queries = {};

interface BaselineLivestock {
  payload: SRecoverable<Array<SSa2Wrapper<ComponentAnnualResponse>>>;
}
interface BaselineFertiliser {
  payload: SRecoverable<Array<SSa2Wrapper<ComponentTotalResponse>>>;
}
interface BaselineLime {
  payload: SRecoverable<SSa2Wrapper<ComponentAnnualResponse>>;
}
interface BaselineResidues {
  payload: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
}
interface BaselineIrrigation {
  payload: SRecoverable<Array<SSa2Wrapper<ComponentAnnualResponse>>>;
}
interface ProjectLivestock {
  payload: SRecoverable<Array<SSa2Wrapper<ComponentAnnualResponse>>>;
}
interface ProjectFertiliser {
  payload: SRecoverable<Array<SSa2Wrapper<ComponentTotalResponse>>>;
}
interface ProjectLime {
  payload: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
}
interface ProjectResidues {
  payload: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
}
interface ProjectIrrigation {
  payload: SRecoverable<SSa2Wrapper<ComponentTotalResponse>>;
}
interface Project {
  payload: SRecoverable<SoilProjectResponse>;
}
interface VegProject {
  payload: SRecoverable<VegProjectResponse>;
}

const querieSlice = createSlice({
  name: "queries",
  initialState: initialState,
  reducers: {
    setBaselineLivestock(state, action: BaselineLivestock) {
      state.baselineLivestock = action.payload;
    },
    setBaselineFertiliser(state, action: BaselineFertiliser) {
      state.baselineFertiliser = action.payload;
    },
    setBaselineLime(state, action: BaselineLime) {
      state.baselineLime = action.payload;
    },
    setBaselineResidues(state, action: BaselineResidues) {
      state.baselineResidues = action.payload;
    },
    setBaselineIrrigation(state, action: BaselineIrrigation) {
      state.baselineIrrigation = action.payload;
    },
    setProjectLivestock(state, action: ProjectLivestock) {
      state.projectLivestock = action.payload;
    },
    setProjectFertiliser(state, action: ProjectFertiliser) {
      state.projectFertiliser = action.payload;
    },
    setProjectLime(state, action: ProjectLime) {
      state.projectLime = action.payload;
    },
    setProjectResidues(state, action: ProjectResidues) {
      state.projectResidues = action.payload;
    },
    setProjectIrrigation(state, action: ProjectIrrigation) {
      state.projectIrrigation = action.payload;
    },
    setProject(state, action: Project) {
      state.project = action.payload;
    },
    setVegProject(state, action: VegProject) {
      state.vegProject = action.payload;
    }
  }
});

const { actions, reducer } = querieSlice;
// Extract and export each action creator by name
export const {
  setBaselineLivestock,
  setBaselineFertiliser,
  setBaselineLime,
  setBaselineResidues,
  setBaselineIrrigation,
  setProjectLivestock,
  setProjectFertiliser,
  setProjectLime,
  setProjectResidues,
  setProjectIrrigation,
  setProject,
  setVegProject
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
